// Simple validation helpers for Fundmore frontend.

/**
 * Basic email validation using a conservative regex.
 * Matches the general pattern local@domain.tld.
 */
export const validateEmail = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  const email = value.trim();
  // Very simple RFC-like check; good enough for client-side gating.
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Mobile/phone number validation (PROD-573).
 * Accepts common formatting characters — spaces, dashes, dots and
 * parentheses — plus an optional leading "+" country code.
 * Without a country code the number must be exactly 10 digits;
 * with a "+" country code the total digit count may be 11–15 (E.164 cap).
 */
export const validatePhoneNumber = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  const phone = value.trim();
  // Strip formatting, then reject anything but an optional "+" and digits.
  const sanitized = phone.replace(/[\s.\-()]/g, '');
  if (!/^\+?[0-9]+$/.test(sanitized)) {
    return false;
  }
  const digits = sanitized.replace(/\D/g, '');
  if (sanitized.startsWith('+')) {
    return digits.length >= 11 && digits.length <= 15;
  }
  return digits.length === 10;
};

/**
 * Canadian postal code validation (PROD-546/547/548).
 * Format A1A 1A1 — letter-digit-letter, digit-letter-digit — with the
 * middle space optional. Max 7 characters including the space.
 */
export const validatePostalCode = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  const postal = value.trim();
  if (postal.length > 7) {
    return false;
  }
  return /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(postal);
};

/**
 * SIN input formatter: digits only, dashed into the Canadian 3-3-3
 * grouping as the user types (123456789 → 123-456-789). Also used to
 * normalize stored values for display. Keeps at most 9 digits.
 */
export const formatSIN = (value) => {
  const digits = String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, 9);
  const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)];
  return parts.filter(Boolean).join('-');
};

/**
 * Canadian SIN validation: exactly 9 digits. Accepts all three common
 * input formats — 123456789, 123 456 789, 123-456-789. No checksum:
 * FundMore's spec is 9 digits in any of these formats, nothing stricter.
 */
export const validateSIN = (value) => {
  if (value == null) return false;
  const digits = String(value).replace(/[\s-]/g, '');
  return /^\d{9}$/.test(digits);
};

/**
 * Postal code input formatter: uppercases and inserts the middle space
 * (A1A1A1 → A1A 1A1) as the user types, so hand-typed values match the
 * Google-autofilled format. Keeps at most 6 alphanumerics.
 */
export const formatPostalCode = (value) => {
  const chars = String(value ?? '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, 6);
  return chars.length > 3 ? `${chars.slice(0, 3)} ${chars.slice(3)}` : chars;
};

/**
 * Account number validation (PROD-550).
 * Numeric only, 1-15 digits.
 */
export const validateAccountNumber = (value) => {
  if (value === null || value === undefined) {
    return false;
  }
  return /^\d{1,15}$/.test(String(value).trim());
};

